import { computed, ref } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { deepCopy } from "@utils/toolBox";
import genToken from "@utils/generateToken";
import { useMCampaignStore } from "@stores/mCampaign";

export const useMGroupsStore = defineStore("mGroups", () => {
  const mCampaignStore = useMCampaignStore();
  const { currentMCampaign } = storeToRefs(mCampaignStore);

  const _selectedMGroupIndex = ref(0);

  const _selectedMGroup = computed(() => currentMCampaign.value.m_groups[_selectedMGroupIndex.value]);
  const mGroups = computed(() => currentMCampaign.value.m_groups);

  const selectMGroup = (index) => {
    const mGroup = mGroups.value.at(index);
    if (!mGroup) return;

    _selectedMGroupIndex.value = index;
  };

  const isMGroupSelected = (index) => _selectedMGroupIndex.value === index;

  const isRemovableMGroup = (mGroup) =>
    mGroups.value.length > 1 && !(currentMCampaign.value.pushed_group_tokens ?? []).includes(mGroup.token);

  const addMGroup = async () => {
    const newMGroup = {
      token: genToken(),
      name: `Campaign ${mGroups.value.length + 1}`,
      status: "active",
      selected_audiences: [],
      selected_creatives: [],
    };
    mGroups.value.push(newMGroup);
    selectMGroup(mGroups.value.length - 1);
  };

  const removeMGroup = (index) => {
    const mGroup = mGroups.value.at(index);
    if (!mGroup) return;

    clearMGroupAds(mGroups.value[index].token);
    mGroups.value.splice(index, 1);
    selectMGroup(Math.max(index - 1, 0));
  };

  const clearMGroupAds = (groupToken) => {
    currentMCampaign.value.m_ads = currentMCampaign.value.m_ads.filter((a) => a.group_token !== groupToken);
  };

  const duplicateMGroup = (index) => {
    const newMGroup = deepCopy(mGroups.value.at(index));
    const newIndex = index + 1;
    newMGroup.token = genToken();
    newMGroup.name = `${newMGroup.name} Copy`;
    mGroups.value.splice(newIndex, 0, newMGroup);
    selectMGroup(newIndex);
  };

  const cleanMGroups = () => {
    mGroups.value.forEach((group) => {
      group.selected_audiences = currentMCampaign.value.m_audiences
        .filter((a) => group.selected_audiences.includes(a.token) && a.status === "active")
        .map((a) => a.token);
      group.selected_creatives = currentMCampaign.value.m_creatives
        .filter((c) => group.selected_creatives.includes(c.token) && c.status === "active")
        .map((c) => c.token);
    });
  };

  return {
    _selectedMGroup,
    mGroups,
    selectMGroup,
    isMGroupSelected,
    addMGroup,
    removeMGroup,
    duplicateMGroup,
    isRemovableMGroup,
    cleanMGroups,
  };
});
